/**
 * Authentication store using Zustand for Scalekit OAuth
 * Frontend is unaware of login implementation - uses /api/auth/me
 */
import { create } from 'zustand';
import { authService } from '../services/auth';
import type { AuthState, User } from '../types';

interface AuthStore extends AuthState {
  setAuth: (user: User) => void;
  redirectToLogin: (organizationId?: string) => Promise<void>;
  logout: () => Promise<void>;
  initialize: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

export const useAuthStore = create<AuthStore>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,

  initialize: async () => {
    set({ isLoading: true });
    try {
      const user = await authService.getMe();

      if (user) {
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
        });
      } else {
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    } catch (error) {
      console.error('Error initializing auth:', error);
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  },

  setAuth: (user: User) => {
    set({
      user,
      isAuthenticated: true,
      isLoading: false,
    });
  },

  redirectToLogin: async (organizationId?: string) => {
    await authService.redirectToLogin(organizationId);
  },

  logout: async () => {
    await authService.logout();
    set({
      user: null,
      isAuthenticated: false,
      isLoading: false,
    });
  },

  refreshSession: async () => {
    set({ isLoading: true });
    try {
      const user = await authService.getMe();

      if (user) {
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
        });
      } else {
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    } catch (error) {
      console.error('Error refreshing session:', error);
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  },
}));
